import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BentoGrid, BentoCard } from '../ui/bento-grid';
import { useResponsive } from '../../hooks/use-responsive';

// iOS风格的弹簧动画配置
const iosSpringConfig = {
  type: "spring",
  stiffness: 300,
  damping: 25,
  mass: 0.8
};

// 桌面端卡片动画变体（保持复杂动画）
const desktopCardVariants = {
  hidden: { 
    opacity: 0, 
    y: 30, 
    scale: 0.95,
    rotateX: 10
  },
  visible: (index) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    rotateX: 0,
    transition: {
      ...iosSpringConfig,
      delay: index * 0.1,
      duration: 0.8
    }
  }),
  exit: {
    opacity: 0,
    y: -20,
    scale: 0.95,
    transition: {
      duration: 0.4,
      ease: "easeInOut"
    }
  }
};

// 移动端简化的动画变体
const mobileCardVariants = {
  hidden: { 
    opacity: 0, 
    y: 20
  },
  visible: (index) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      delay: index * 0.1,
      ease: "easeOut"
    }
  }),
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  }
};

// 桌面端容器动画变体
const desktopContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      ...iosSpringConfig,
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  },
  exit: {
    opacity: 0,
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1
    }
  }
};

// 移动端容器动画变体
const mobileContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.05
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  }
};

const ResponsiveBentoGrid = ({ features, hasSaved }) => {
  // 添加错误边界保护，防止 useResponsive 钩子失败
  let isMobile = false;
  try {
    const responsive = useResponsive();
    isMobile = responsive?.isMobile || false;
  } catch (error) {
    console.warn('useResponsive hook error in ResponsiveBentoGrid:', error);
    // 如果钩子失败，默认使用桌面端布局
    isMobile = false;
  }

  // 只有移动端才使用这个组件的自定义布局
  if (isMobile) {
    // 移动端使用自定义布局
    const searchCard = features.find(f => f.name === 'ApiKeyQuery');
    const savedCard = features.find(f => f.name === 'SavedApiKey');
    const serviceCard = features.find(f => f.name === 'ServiceStatus');
    const modelsCard = features.find(f => f.name === 'SupportedModels' || f.name === '没有Key？立即购买！' || f.name === '余额不足了？立即购买！');

    return (
      <AnimatePresence mode="wait">
        <motion.div
          key={`mobile-layout-${hasSaved ? 'saved' : 'no-saved'}`}
          className="flex flex-col gap-6 w-full"
          variants={mobileContainerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* 主要搜索功能 */}
          {searchCard && (
            <motion.div
              variants={mobileCardVariants}
              custom={0}
            >
              <BentoCard 
                {...searchCard}
                className="w-full min-h-[280px] rounded-2xl"
              />
            </motion.div>
          )}

          {/* 历史记录（如果有） */}
          <AnimatePresence>
            {savedCard && hasSaved && (
              <motion.div
                key="saved-card"
                variants={mobileCardVariants}
                custom={1}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <BentoCard 
                  {...savedCard}
                  className="w-full min-h-[160px] rounded-xl"
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* 底部并排区域 */}
          <motion.div 
            className="flex gap-4 w-full"
            variants={mobileCardVariants}
            custom={2}
          >
            {serviceCard && (
              <BentoCard 
                {...serviceCard}
                className="flex-1 min-h-[140px] rounded-xl"
              />
            )}
            {modelsCard && (
              <BentoCard 
                {...modelsCard}
                className="flex-1 min-h-[140px] rounded-xl"
              />
            )}
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // 桌面端需要根据hasSaved状态调整布局
  const visibleFeatures = features.filter(feature => {
    // 过滤掉隐藏的SavedApiKey组件
    if (feature.name === 'SavedApiKey' && !hasSaved) {
      return false;
    }
    return true;
  });

  return (
    <BentoGrid 
      className={!hasSaved ? "!grid-cols-3 !grid-rows-2" : undefined} // 没有Key时使用3x2布局
      style={hasSaved ? {
        gridTemplateRows: '2fr 1.2fr'
      } : {
        gridTemplateRows: '1.5fr 1fr' // 没有Key时调整行高比例
      }}
    >
      {visibleFeatures.map((feature, idx) => {
        // 当没有保存的Key时，调整布局
        if (!hasSaved) {
          if (feature.name === 'ApiKeyQuery') {
            // 查询卡片跨越三列，占据第一行
            return (
              <BentoCard 
                key={`desktop-${feature.name || idx}`}
                {...feature}
                className={`${feature.className || ''} col-span-3 row-span-1`.trim()}
              />
            );
          } else if (feature.name === 'ServiceStatus') {
            // 服务状态卡片占据第二行左边，跨越两列
            return (
              <BentoCard 
                key={`desktop-${feature.name || idx}`}
                {...feature}
                className={`${feature.className || ''} col-span-2 row-span-1`.trim()}
              />
            );
          } else if (feature.name === 'SupportedModels' || feature.name === '没有Key？立即购买！' || feature.name === '余额不足了？立即购买！') {
            // 模型卡片占据第二行右边，一列
            return (
              <BentoCard 
                key={`desktop-${feature.name || idx}`}
                {...feature}
                className={`${feature.className || ''} col-span-1 row-span-1`.trim()}
              />
            );
          }
        }
        
        return (
          <BentoCard 
            key={`desktop-${feature.name || idx}`}
            {...feature} 
          />
        );
      })}
    </BentoGrid>
  );
};

export default ResponsiveBentoGrid; 